import * as qs from "query-string";
import { useMemo } from "react";
import { Speciality } from "../types/Speciality";

type BoardingType = "" | "day" | "boarding";
type Gender = "" | "both" | "boys" | "girls";
type Age = "nursery" | "pre_prep" | "prep" | "senior" | "sixth_form";

export const useQueryParams = (qsOverride: string | null) => {
    const parsed = qs.parse(qsOverride || location.search);

    return useMemo(() => createParamGetter(parsed), [parsed]);
};

const createParamGetter = (params: qs.ParsedQuery) => ({
    getQuery(): string {
        return Array.isArray(params.q) ? "" : params.q || "";
    },

    getBoardingType(): BoardingType {
        const { boarding_type } = params;

        if (Array.isArray(boarding_type)) {
            return "";
        }

        if (boarding_type !== "day" && boarding_type !== "boarding") {
            return "";
        }

        return boarding_type;
    },

    getGender(): Gender {
        const { gender } = params;

        if (Array.isArray(gender)) {
            return "";
        }

        if (gender !== "both" && gender !== "boys" && gender !== "girls") {
            return "";
        }

        return gender;
    },

    getLocation(): string {
        if (Array.isArray(params.location)) {
            return "";
        }

        return params.location || "";
    },

    getRadius(): number {
        if (Array.isArray(params.radius)) {
            return 10;
        }

        return params.radius ? parseInt(params.radius, 10) : 10;
    },

    getAges(): Age[] {
        if (Array.isArray(params.ages)) {
            return [];
        }

        if (!params.ages || params.ages == "any") {
            return [];
        }

        const split = params.ages
            .split(",")
            .filter<Age>(
                (item): item is Age =>
                    item === "nursery" ||
                    item === "pre_prep" ||
                    item === "prep" ||
                    item === "senior" ||
                    item === "sixth_form"
            );

        return split;
    },

    getRegions(): string[] {
        if (
            !params.regions ||
            params.regions == "all" ||
            Array.isArray(params.regions)
        ) {
            return [];
        }

        return params.regions.split(",");
    },

    getCountries(): string[] {
        if (
            !params.countries ||
            params.countries == "all" ||
            Array.isArray(params.countries)
        ) {
            return [];
        }

        return params.countries.split(",");
    },

    getCurricula(): string[] {
        if (
            !params.curricula ||
            params.curricula == "all" ||
            Array.isArray(params.curricula)
        ) {
            return [];
        }

        return params.curricula.split(",");
    },

    getProgrammes(): string[] {
        if (
            !params.programmes ||
            params.programmes == "all" ||
            Array.isArray(params.programmes)
        ) {
            return [];
        }

        return params.programmes.split(",");
    },

    getSpeciality(): Speciality[] {
        if (
            !params.speciality ||
            params.speciality == "any" ||
            Array.isArray(params.speciality)
        ) {
            return [];
        }

        return params.speciality
            .split(",")
            .filter<Speciality>(
                (item): item is Speciality =>
                    item === "" ||
                    item === "social" ||
                    item === "learning" ||
                    item === "emotional" ||
                    item === "sensory"
            );
    }
});
